import { FleetDataItem } from "../types/FleetDataItem";
import { Inspection } from "../types/Inspection";
import { ID_PLATFORM_INSPECTION_STATUS_MAPPING } from "@idwal/idwal-utilities/lib/types/AsimsStatusMapping";
import { getTranslationObject } from "../services/Translation";
import { getVesselGrade, GradeType } from "@idwal/idwal-react-components";
import { calculateDaysSince, convertInspectionDates, rankData, setGlobalFilterFields } from "./FleetHelper";
import { InternalGradeData } from "../types/InternalGradeData";
import { INSPECTION_STATUS_UPCOMING } from "./Constants";
import { string } from "prop-types";

function handleUninspectedVesselDueDate(fleetDataItem: FleetDataItem) {
    if (!fleetDataItem.inspection?.inspectionDue) {
        // This handles the scenario where a vessel has never been inspected, but a user defined scheduled date is defined in fleet config for the vessel.
        // Column in the table looks at one field. This is the simplest way to get the data into the correct format for the table.
        if (fleetDataItem.scheduledDate) {
            fleetDataItem.inspection = {};
            fleetDataItem.inspection.inspectionDue = fleetDataItem.scheduledDate;
            fleetDataItem.inspectionDateFilterField = fleetDataItem.scheduledDate;
        }
    }
}

export function addCustomFieldFleetFiltersToData (data: Array<FleetDataItem>) {
    data.forEach(x => {
        x.nameFilterField = x.vessel.vesselName + " " + x.vessel.imo;

        if (x.vesselGrade) {
            if (x.vesselGrade.gradeType === GradeType.IDWAL) {
                x.gradeFilterField = x.vesselGrade.grade;
            } else if (x.vesselGrade.grade) {
                x.gradeFilterField = x.vesselGrade.grade;
            }
        }

        handleUninspectedVesselDueDate(x);

        if (x.inspection.isScheduled) {
            if (x.inspection.dateTo) {
                x.inspectionDateFilterField = new Date(x.inspection.dateTo).toLocaleDateString("en-GB") + " " + new Date(x.inspection.dateFrom!).toLocaleDateString("en-GB");
            } else {
                x.inspectionDateFilterField = new Date(x.inspection.dateFrom!).toLocaleDateString("en-GB");
            }
        } else if (x.inspection.inspectionDue) {
            const inspectionDueDate = x.inspection.inspectionDue;
            x.inspectionDateFilterField = inspectionDueDate;
        }

        const daysSince =  x.inspection.daysSince;

        if (daysSince !== null && daysSince !== undefined && daysSince >= 0) {
            const daysSinceInspectionDate = new Date();
            daysSinceInspectionDate.setDate(daysSinceInspectionDate.getDate() - daysSince);
            x.dateSinceFilterField = daysSinceInspectionDate.toLocaleDateString("en-GB");
        }

        if (x.vessel?.specialSurveyDate) {
            x.vessel.specialSurveyDate = new Date(x.vessel.specialSurveyDate);
            x.specialSurveyDateFilterField = x.vessel?.specialSurveyDate.toLocaleDateString("en-GB");
        }

        x.vesselLatestInspectionStatus = 0;
        if (x.inspection?.isScheduled) {
            x.vesselLatestInspectionStatus = x.inspection.scheduledInspectionStatus
        } else {
            if (x.inspection?.hasUpcoming) {
                x.vesselLatestInspectionStatus = INSPECTION_STATUS_UPCOMING
            } else {
                x.vesselLatestInspectionStatus = x.inspection?.status;
            }
        }
    });

    return data;
}

export function addInspectionDataToTableData(fleetDataItem: FleetDataItem, inspectionAndGradeResponse: FleetDataItem[], internalGrades: InternalGradeData[] | undefined, hasAiGradingCapability: boolean) {
    const key = fleetDataItem.vessel.imo;

    let fleetDataFromApi = inspectionAndGradeResponse.find(item => item.vessel.imo === key);
    if (!fleetDataFromApi) {
        fleetDataFromApi = {
            vessel: fleetDataItem.vessel,
            inspection: {}
        }
    }

    if (fleetDataFromApi.inspection && Object.keys(fleetDataFromApi.inspection).length != 0) {
        fleetDataItem.inspection = fleetDataFromApi.inspection;
    }

    if (!fleetDataItem.scheduledDate && fleetDataFromApi.scheduledDate) {
        fleetDataItem.scheduledDate = fleetDataFromApi.scheduledDate;
    }
    fleetDataItem.rank = fleetDataFromApi.rank;

    if(fleetDataFromApi.scoreTrends && fleetDataFromApi.inspection.latestGrades?.idwalGrade != null) {
        fleetDataItem.trending = fleetDataFromApi.scoreTrends.idwalScoreTrend;
        fleetDataItem.trendingLastInspected = fleetDataFromApi.scoreTrends.lastInspected;
    }

    if (hasAiGradingCapability) {
        const internalGradeData = internalGrades?.find((item: InternalGradeData) => item.vessel_imo.toString() === key.toString());
        fleetDataItem.aiGrades = {
            grade: internalGradeData?.grade,
            gradeType: internalGradeData?.grade_type,
            gradeTypeDisplay: internalGradeData?.grade_type_display,
            aiGradeFilterField: internalGradeData?.grade?.toString() ?? "",
            gradeDateDisplay: internalGradeData?.grade_date,
        }
    }

    fleetDataItem.vesselGrade = getVesselGrade(
        fleetDataItem.inspection?.latestGrades?.idwalGrade ? fleetDataItem.inspection?.latestGrades?.idwalGrade.toString() : undefined, fleetDataItem.inspection?.status?.toString());

    fleetDataItem.scoreHistory = fleetDataFromApi.scoreHistory;

    if (!fleetDataItem.vesselGrade) {
        fleetDataItem.gradeToDisplay = undefined;
        fleetDataItem.gradeFilterField = undefined;
        return;
    }

    fleetDataItem.gradeToDisplay = fleetDataItem.vesselGrade.grade?.toString();

    fleetDataItem.aiGrades = {
        ...fleetDataItem.aiGrades,
        aiGradeFilterField: fleetDataItem.vesselGrade.gradeFilterField
    };
};

/**
 * Resolves the inspection type key regardless of which shape the API used:
 * the primary inspection nests it in an object, while score-history rows carry
 * the display name as a string alongside a separate short name.
 */
export function getInspectionTypeKey(inspection: Inspection | undefined): string {
    const { inspectionType } = inspection ?? {};

    if (typeof inspectionType === "object" && inspectionType?.shortName) {
        return inspectionType.shortName;
    }

    if (inspection?.inspectionTypeShortName) {
        return inspection.inspectionTypeShortName;
    }

    return typeof inspectionType === "string" ? inspectionType : "";
}

/**
 * A newer inspection of the same type supersedes the older one, so both the
 * inspection lists and the defects pipeline rely on this to keep only the latest.
 */
export function filterScoreHistoryByType(scoreHistory: Inspection[] | undefined) {
    if (!scoreHistory || scoreHistory.length === 0) {
        return [];
    }

    const latestInspections: Record<string, Inspection> = {};

    // A row without a usable date must never displace a dated inspection of the same type.
    const inspectedAt = (inspection: Inspection) => {
        const time = new Date(inspection.lastInspected as string).getTime();
        return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
    };

    scoreHistory.forEach((item) => {
        const inspectionType = getInspectionTypeKey(item);
        const current = latestInspections[inspectionType];

        if (!current || inspectedAt(item) > inspectedAt(current)) {
            latestInspections[inspectionType] = item;
        }
    });

    return Object.values(latestInspections);
}

export function mutateFleetDataWithInspections(
    fleetDataItems: FleetDataItem[],
    inspectionAndGradeResponse: FleetDataItem[],
    internalGrades: InternalGradeData[] | undefined,
    hasAiGradingCapability: boolean
) {
    convertInspectionDates(inspectionAndGradeResponse);
    calculateDaysSince(inspectionAndGradeResponse);

    inspectionAndGradeResponse = rankData(inspectionAndGradeResponse);

    fleetDataItems.forEach((fleetDataItem: FleetDataItem) => {
        addInspectionDataToTableData(fleetDataItem, inspectionAndGradeResponse, internalGrades, hasAiGradingCapability);
    });

    setGlobalFilterFields(fleetDataItems);
}

export function getFriendlyInspectionStatusText(inspectionStatus: number) {
    const t = getTranslationObject();
    let statusGroup;
    
    if (inspectionStatus !== null && inspectionStatus !== undefined && ID_PLATFORM_INSPECTION_STATUS_MAPPING[inspectionStatus]?.translationKey) {
        statusGroup = ID_PLATFORM_INSPECTION_STATUS_MAPPING[inspectionStatus].translationKey;
    }

    return statusGroup? t(`inspectionStatuses.${statusGroup}`) : t(`inspectionStatuses.neverInspected`);
}
